import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Loader2, AlertTriangle, CheckCircle2, Download, Globe } from 'lucide-react';
import api from '../../api/client';

/**
 * PRD v2.1 / Slice S8 — Cross-org marketplace import dialog.
 *
 * Two-step UX:
 *   1. Confirm: shows source-org + course + what's copied / what's stripped.
 *   2. Progress: polls the job until done|failed, then routes to the new course.
 */
const ImportMarketplaceCourseDialog = ({
  open,
  onOpenChange,
  consumerOrgId,
  sourceOrg,
  course,
  onImported,
}) => {
  const [phase, setPhase] = useState('confirm'); // confirm | running | done | failed
  const [jobId, setJobId] = useState(null);
  const [jobDoc, setJobDoc] = useState(null);
  const [error, setError] = useState(null);
  const pollTimerRef = useRef(null);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setPhase('confirm');
      setJobId(null);
      setJobDoc(null);
      setError(null);
    } else if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [open]);

  const pollJob = async (jid) => {
    try {
      const resp = await api.get(
        `/organizations/${consumerOrgId}/marketplace/import-jobs/${jid}`,
      );
      const job = resp.data || {};
      setJobDoc(job);
      if (job.status === 'done') {
        setPhase('done');
        return;
      }
      if (job.status === 'failed') {
        setError(job.error || 'Import failed');
        setPhase('failed');
        return;
      }
      pollTimerRef.current = setTimeout(() => pollJob(jid), 2000);
    } catch (_e) {
      pollTimerRef.current = setTimeout(() => pollJob(jid), 3000);
    }
  };

  const handleConfirm = async () => {
    setPhase('running');
    setError(null);
    try {
      const resp = await api.post(
        `/organizations/${consumerOrgId}/marketplace/import`,
        { sourceOrgId: sourceOrg.id, sourceCourseId: course.id },
      );
      const jid = resp.data?.jobId;
      if (!jid) {
        setError('Server did not return a job id.');
        setPhase('failed');
        return;
      }
      setJobId(jid);
      pollJob(jid);
    } catch (e) {
      setError(e?.message || 'Failed to start import');
      setPhase('failed');
    }
  };

  const handleClose = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    onOpenChange(false);
  };

  const handleOpenImported = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    const newId = jobDoc?.result?.importedCourseId || jobDoc?.importedCourseId;
    onImported && onImported(newId, jobDoc?.result);
    onOpenChange(false);
  };

  const result = jobDoc?.result || {};

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg"
        data-testid="marketplace-import-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-sky-600" />
            Import course from marketplace
          </DialogTitle>
          <DialogDescription>
            A copy of this course will be cloned into your organization. You will
            own the copy outright — subsequent edits on either side stay isolated.
          </DialogDescription>
        </DialogHeader>

        {phase === 'confirm' && (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3 space-y-1">
              <div className="flex items-center gap-2 text-sky-900 font-medium">
                <Globe className="w-4 h-4" /> {course?.title}
              </div>
              <div className="text-sky-800 text-xs">
                From <span className="font-medium">{sourceOrg?.name}</span>
                {course?.difficulty ? ` · ${course.difficulty}` : ''}
                {course?.domain ? ` · ${course.domain}` : ''}
              </div>
            </div>
            <div>
              <div className="font-medium mb-1">What gets copied</div>
              <ul className="list-disc pl-5 text-gray-700 space-y-0.5 text-xs">
                <li>The full course structure — topics, content blocks, ordering.</li>
                <li>Uploaded files & generated PDFs (duplicated under your org).</li>
                <li>Block configuration (labs, quizzes, infographics).</li>
              </ul>
            </div>
            <div>
              <div className="font-medium mb-1">What gets stripped</div>
              <ul className="list-disc pl-5 text-gray-700 space-y-0.5 text-xs">
                <li>The source org's AI keys & raw prompts.</li>
                <li>Generation lineage (Gamma job ids, Remotion render ids).</li>
                <li>AI-generated blocks will be marked <em>regen-requires-own-keys</em>.</li>
              </ul>
            </div>
            <p className="text-xs text-gray-500">
              The imported course lands in your <strong>Populated</strong> tab as
              a template (not published). You can launch cohorts from it just like
              any course you authored.
            </p>
          </div>
        )}

        {phase === 'running' && (
          <div className="py-6 space-y-3" data-testid={`marketplace-import-progress-${jobId || 'pending'}`}>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Loader2 className="w-4 h-4 animate-spin" />
              {jobDoc?.status === 'queued' ? 'Queued…' : 'Cloning course, topics, blocks and files…'}
            </div>
            <Progress value={jobDoc?.status === 'running' ? 60 : 20} />
            <p className="text-xs text-gray-500">
              Large courses with many uploaded PDFs may take a minute. You can
              keep this dialog open or close it — the job continues in the
              background.
            </p>
          </div>
        )}

        {phase === 'done' && (
          <div className="py-4 space-y-3 text-sm" data-testid="marketplace-import-done">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Import complete</span>
            </div>
            {result.alreadyImported ? (
              <p className="text-gray-700">
                This course was already imported earlier. We've opened the existing copy.
              </p>
            ) : (
              <ul className="text-gray-700 text-xs list-disc pl-5 space-y-0.5">
                <li>{result.topicCount ?? 0} topics cloned</li>
                <li>{result.blockCount ?? 0} content blocks cloned</li>
                <li>{result.filesCopied ?? 0} files duplicated under your org</li>
                {result.aiBlocksFlagged ? (
                  <li className="text-amber-700">
                    {result.aiBlocksFlagged} AI-generated block{result.aiBlocksFlagged === 1 ? '' : 's'} need your org's API keys before regen.
                  </li>
                ) : null}
              </ul>
            )}
          </div>
        )}

        {phase === 'failed' && (
          <div className="py-4 space-y-2 text-sm" data-testid="marketplace-import-failed">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Import failed</span>
            </div>
            <p className="text-gray-700 text-xs">{error || 'Unknown error'}</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {phase === 'confirm' && (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                data-testid="marketplace-import-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                data-testid="marketplace-import-confirm-yes"
              >
                Import to my org
              </Button>
            </>
          )}
          {phase === 'running' && (
            <Button
              variant="outline"
              onClick={handleClose}
              data-testid="marketplace-import-close-running-btn"
            >
              Close (continues in background)
            </Button>
          )}
          {phase === 'done' && (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                data-testid="marketplace-import-stay-btn"
              >
                Stay on marketplace
              </Button>
              <Button
                onClick={handleOpenImported}
                data-testid="marketplace-import-open-course-btn"
              >
                Open imported course
              </Button>
            </>
          )}
          {phase === 'failed' && (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                data-testid="marketplace-import-fail-close-btn"
              >
                Close
              </Button>
              <Button
                onClick={() => { setPhase('confirm'); setError(null); }}
                data-testid="marketplace-import-retry-btn"
              >
                Try again
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportMarketplaceCourseDialog;
